import React from "react";
import { Box } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";
import { COLORS } from "../../assets/theme";
import CustomTable from "../../components/table/CustomTable";
import SearchHeaderContainer from "../../components/table/SearchHeaderContainer";
import GenericFormDialog, { Field } from "../../components/GenericFormDialog";
import CustomLabel from "../../components/CustomLabel";
import PageHeader from "../../components/PageHeader";
import CustomButton from "../../components/CustomButton";
import CustomDialog from "../../components/CustomDialog";
import Actions from "../../components/Actions";
import InvoiceItemsEditor, { InvoiceItem } from "../../components/invoices/InvoiceItemsEditor";
import PurchaseItemsEditor from "../../components/purchases/PurchaseItemsEditor";
import type {
  ExpenseItemInput,
  CreateExpensePayload,
} from "../../services/newExpensesServices";
import { getAllBranches } from "../../services/BranchesService";
import { useParams, useLocation } from "react-router-dom";
import {
  createExpense,
  listExpenses,
  payExpense,
  approveExpense,
  updateExpense,
  deleteExpense,
} from "../../services/newExpensesServices";
import { listAccounts } from "../../services/newAccountsServices";
import { formatTableDate } from "../../utils/tableDateFormat";
import { fetchProducts, createProduct } from "../../services/ProductsNewService";
import CustomSelect from "../../components/CustomSelect";
import { fetchSuppliers } from "../../services/SuppliersService";
import CustomTextField from "../../components/CustomTextField";

const getErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

const showErrorToast = (message: any) =>
  toast.error(String(message), { toastId: String(message) });

const pickNonEmptyString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
    }
  }
  return undefined;
};

const Expenses: React.FC = () => {
  interface Account {
    id: number;
    code: string;
    name: string;
    type: "EXPENSE" | "REVENUE" | "CASH" | "PAYABLE" | "RECEIVABLE";
  }
  //const businessId = location.state?.businessId || params.businessId || "";
  const params = useParams<{ businessId?: string; id?: string }>();
  const businessId = params.businessId || params.id;

  if (!businessId) {
    throw new Error("Clients page requires businessId in route");
  }

  const [editOpen, setEditOpen] = React.useState(false);
  const [selectValue, setSelectValue] = React.useState<
    string | number | (string | number)[]
  >("all");

  const [accounts, setAccounts] = React.useState<any[]>([]);
  const [loadingAccounts, setLoadingAccounts] = React.useState(false);
  const [search, setSearch] = React.useState<string>("");
  const [units, setUnits] = React.useState<any[]>([]);
  const [loadingUnits, setLoadingUnits] = React.useState(false);
  const [editingRow, setEditingRow] = React.useState<any | null>(null);
  const [expenseDialogTab, setExpenseDialogTab] = React.useState(0);
  const [products, setProducts] = React.useState<any[]>([]);
  const [loadingProducts, setLoadingProducts] = React.useState(false);
  const [productDialogOpen, setProductDialogOpen] = React.useState(false);
  const [suppliers, setSuppliers] = React.useState<any[]>([]);
  const [loadingSuppliers, setLoadingSuppliers] = React.useState(false);
  const [dateRange, setDateRange] = React.useState<
    { start?: string; end?: string } | [string?, string?] | null
  >(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    return { start: start.toISOString(), end: end.toISOString() };
  });

  const getZonedParts = (date: Date, timeZone: string) => {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(date);

    return parts.reduce<Record<string, string>>((acc, part) => {
      if (part.type !== "literal") {
        acc[part.type] = part.value;
      }
      return acc;
    }, {});
  };

  const buildDateWithCurrentTime = (dateInput?: string) => {
    const tz = "Asia/Beirut";
    const now = new Date();
    const baseDate = dateInput ? new Date(dateInput) : new Date();
    if (isNaN(baseDate.getTime())) return now.toISOString();

    const dateParts = getZonedParts(baseDate, tz);
    const timeParts = getZonedParts(now, tz);

    const beirutDateUtc = new Date(
      Date.UTC(
        Number(dateParts.year),
        Number(dateParts.month) - 1,
        Number(dateParts.day),
        Number(timeParts.hour),
        Number(timeParts.minute),
        Number(timeParts.second),
        now.getMilliseconds(),
      ),
    );

    return beirutDateUtc.toISOString();
  };

  const buildDateBoundary = (raw: string | Date | null | undefined, endOfDay: boolean) => {
    if (!raw) return null;
    if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [y, m, d] = raw.split("-").map(Number);
      return new Date(Date.UTC(y, m - 1, d, endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, endOfDay ? 999 : 0));
    }
    const parsed = raw instanceof Date ? raw : new Date(raw);
    if (Number.isNaN(parsed.getTime())) return null;
    return new Date(
      Date.UTC(
        parsed.getUTCFullYear(),
        parsed.getUTCMonth(),
        parsed.getUTCDate(),
        endOfDay ? 23 : 0,
        endOfDay ? 59 : 0,
        endOfDay ? 59 : 0,
        endOfDay ? 999 : 0,
      ),
    );
  };

  const normalizeDateRange = (
    range: { start?: string; end?: string } | [string?, string?] | null
  ): { start?: string; end?: string } => {
    if (!range) return {};
    const startRaw = Array.isArray(range) ? range[0] : range.start;
    const endRaw = Array.isArray(range) ? range[1] : range.end;

    const start = buildDateBoundary(startRaw as any, false);
    const end = buildDateBoundary(endRaw as any, true);

    return {
      start: start ? start.toISOString() : undefined,
      end: end ? end.toISOString() : undefined,
    };
  };

  const businessDisplayName = React.useMemo(() => {
    const firstUnit = Array.isArray(units) && units.length > 0 ? units[0] : null;
    return (
      pickNonEmptyString(
        firstUnit?.business_name,
        firstUnit?.businessName,
        firstUnit?.business?.name,
        firstUnit?.venue_name,
        firstUnit?.venueName
      ) || (businessId ? `Business ${businessId}` : "Business")
    );
  }, [units, businessId]);

  const defaultExpenseAccountId = React.useMemo(() => {
    const strict = (accounts || []).find(
      (a: any) => String(a?.code || "").toUpperCase() === "EXPENSE",
    );
    if (strict?.id !== undefined && strict?.id !== null) return strict.id;
    const first = (accounts || [])[0];
    return first?.id ?? "";
  }, [accounts]);

  // Only offered on create; editing never changes paid status
  const autoPayFields: Field[] = editingRow
    ? []
    : [{ name: "autoPay", label: "Pay now (auto pay)", type: "switch" }];

  const formFields: Field[] = [
    {
      name: "unitId",
      label: "Unit / Branch",
      type: "dropdown",
      required: true,
      options: [
        { label: `All (${businessDisplayName})`, value: "all" },
        ...units.map((u) => ({
          label: u.name || u.title || u.id || u._id,
          value: String(u.id || u._id),
        })),
      ],
    },
    {
      name: "accountId",
      label: "Account",
      type: "dropdown",
      required: true,
      options: accounts.map((a) => ({
        label: `${a.code} – ${a.name}`,
        value: a.id,
      })),
    },
    { name: "title", label: "Title", required: true },
    {
      name: "description",
      label: "Description",
      required: true,
    },
    {
      name: "amount",
      label: "Amount",
      type: "number",
      required: true,
    },
    { name: "notes", label: "Notes" },
    ...autoPayFields,
  ];

  const productFormFields: Field[] = [
    { name: "name", label: "Name", type: "text", required: true },
    {
      name: "packageType",
      label: "Package Type",
      type: "dropdown",
      options: [
        { label: "Unit", value: "UNIT" },
        { label: "Pack", value: "PACK" },
      ],
      required: true,
      disabled: true,
    },
    { name: "cost_price", label: "Cost Price", type: "number" },
    { name: "sale_price", label: "Sale Price", type: "number" },
  ];
  const purchaseItemOptions = React.useMemo(() => {
    return (Array.isArray(products) ? products : []).map((p: any) => ({
      id: p?.id ?? p?._id,
      name: p?.name ?? p?.title ?? "Product",
      unit_cost: p?.cost_price ?? p?.costPrice ?? p?.price ?? 0,
      type:
        p?.type ??
        p?.product_type ??
        p?.productType ??
        p?.packageType ??
        p?.package_type ??
        "",
    }));
  }, [products]);

  React.useEffect(() => {
    const isPurchaseEdit = Boolean(editingRow?.metadata?.type === "PURCHASE");
    if (!editOpen || (!isPurchaseEdit && expenseDialogTab !== 1)) return;

    const loadSuppliers = async () => {
      setLoadingSuppliers(true);
      try {
        const res = await fetchSuppliers();
        const data = (res as any)?.data ?? res;
        const list = Array.isArray(data) ? data : data?.suppliers || [];
        setSuppliers(list);
      } catch (error) {
        console.error("Failed to load suppliers:", error);
        setSuppliers([]);
      } finally {
        setLoadingSuppliers(false);
      }
    };

    loadSuppliers();
  }, [editOpen, expenseDialogTab, editingRow]);

  const supplierOptions = React.useMemo(() => {
    return (Array.isArray(suppliers) ? suppliers : []).map((s: any) => ({
      label:
        s?.company_name ||
        s?.companyName ||
        s?.supplier_name ||
        s?.supplierName ||
        s?.full_name ||
        s?.fullName ||
        s?.name ||
        "Supplier",
      value: String(s?.id ?? s?._id ?? ""),
      phone:
        s?.supplier_phone ||
        s?.company_phone ||
        s?.phone ||
        s?.mobile ||
        "",
      supplierName:
        s?.supplier_name || s?.supplierName || "",
      companyName:
        s?.company_name || s?.companyName || "",
      orgUserId:
        s?.orgUserId ??
        s?.org_user_id ??
        s?.org_userId ??
        s?.org_userID ??
        s?.user?.id ??
        s?.userId ??
        s?.id ??
        s?._id ??
        null,
    }));
  }, [suppliers]);

  const purchaseFields: Field[] = [
    {
      name: "unitId",
      label: "Unit / Branch",
      type: "dropdown",
      required: true,
      disabled: true,
      options: [
        { label: `All (${businessDisplayName})`, value: "all" },
        ...units.map((u) => ({
          label: u.name || u.title || u.id || u._id,
          value: String(u.id || u._id),
        })),
      ],
    },
    {
      name: "accountId",
      label: "Account",
      type: "dropdown",
      required: true,
      options: accounts.map((a) => ({
        label: `${a.code} – ${a.name}`,
        value: a.id,
      })),
    },
    {
      name: "title",
      label: "Purchases",
      colSpan: 2,
      customRender: (_value, _onChange, helpers) => (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
            alignItems: "center",
          }}
        >
          <Box>
            <CustomLabel text="Title" fontWeight={500} fontSize={14} />
            <CustomTextField fullWidth value="Purchases" disabled />
          </Box>
          <Box>
            <CustomLabel text="Invoice Number" fontWeight={500} fontSize={14} />
            <CustomTextField
              fullWidth
              value={helpers?.values?.invoiceNumber || ""}
              onChange={(e: any) =>
                helpers?.setFieldValue("invoiceNumber", e.target.value)
              }
            />
          </Box>
        </Box>
      ),
    },
    {
      name: "supplierOrgUserId",
      label: "Supplier",
      type: "dropdown",
      required: true,
      colSpan: 2,
      options: [
        { label: "Select supplier", value: "" },
        ...supplierOptions,
      ],
      customRender: (value, _onChange, helpers) => (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
            alignItems: "center",
          }}
        >
          <Box>
            <CustomLabel text="Supplier" fontWeight={500} fontSize={14} />
            <CustomSelect
              label="Supplier"
              value={value || ""}
              options={[
                { label: "Select supplier", value: "" },
                ...(loadingSuppliers
                  ? [{ label: "Loading...", value: "" }]
                  : supplierOptions),
              ]}
              onChange={(v: any) => {
                helpers?.setFieldValue("supplierOrgUserId", v);
                const selected = supplierOptions.find((o) => o.value === v);
                helpers?.setFieldValue(
                  "supplierName",
                  selected?.label || selected?.companyName || selected?.supplierName || ""
                );
                helpers?.setFieldValue("supplierPhone", selected?.phone || "");
              }}
              fullWidth
            />
          </Box>
          <Box>
            <CustomLabel text="Supplier Phone" fontWeight={500} fontSize={14} />
            <CustomTextField fullWidth value={helpers?.values?.supplierPhone || ""} disabled />
          </Box>
        </Box>
      ),
    },
    {
      name: "purchaseNotes",
      label: "Notes",
      colSpan: 2,
      customRender: (_value, _onChange, helpers) => (
        <Box>
          <CustomLabel text="Notes" fontWeight={500} fontSize={14} />
          <CustomTextField
            fullWidth
            value={helpers?.values?.purchaseNotes || ""}
            onChange={(e: any) =>
              helpers?.setFieldValue("purchaseNotes", e.target.value)
            }
          />
        </Box>
      ),
    },
    {
      name: "lines",
      label: "Items",
      colSpan: 2,
      customRender: (_value: any, onChange: (v: any) => void, helpers) => (
        loadingProducts ? (
          <CustomLabel
            text="Loading items..."
            fontWeight={600}
            sx={{ color: "text.secondary" }}
          />
        ) : (
          <PurchaseItemsEditor
            value={_value}
            onChange={onChange}
            vatValue={helpers?.values?.vat || 0}
            onVatChange={(v) => helpers?.setFieldValue("vat", v)}
            discountValue={helpers?.values?.discount || 0}
            onDiscountChange={(v) => helpers?.setFieldValue("discount", v)}
            promoDiscountValue={helpers?.values?.promoDiscount || 0}
            onPromoDiscountChange={(v) =>
              helpers?.setFieldValue("promoDiscount", v)
            }
            promoValue={helpers?.values?.promoValue || 0}
            onPromoValueChange={(v) => helpers?.setFieldValue("promoValue", v)}
            servicesList={purchaseItemOptions}
            isEditMode
            onAddProductClick={() => setProductDialogOpen(true)}
          />
        )
      ),
    },
    ...autoPayFields,
  ];

  const expenseInitialValues = React.useMemo(
    () => ({
      unitId: editingRow?.unitId || "all",
      accountId: editingRow?.accountId || defaultExpenseAccountId || "",
      title: editingRow?.title || "",
      description: editingRow?.description || "",
      amount:
        editingRow?.amount !== undefined && editingRow?.amount !== null
          ? String(editingRow.amount)
          : "",
      notes: editingRow?.notes || "",
      businessName: businessDisplayName,
      supplierName:
        editingRow?.metadata?.supplierName ||
        editingRow?.metadata?.company_name ||
        editingRow?.metadata?.companyName ||
        "",
      supplierPhone:
        editingRow?.metadata?.supplierPhone ||
        editingRow?.metadata?.company_phone ||
        editingRow?.metadata?.companyPhone ||
        "",
      supplierOrgUserId:
        editingRow?.metadata?.supplierOrgUserId ||
        editingRow?.metadata?.supplier_org_user_id ||
        "",
      invoiceNumber:
        editingRow?.metadata?.invoiceNumber ||
        editingRow?.metadata?.invoice_nb ||
        "",
      purchaseNotes: editingRow?.notes || "",
      lines: editingRow?.metadata?.lines || [],
      autoPay: false,
    }),
    [editingRow, defaultExpenseAccountId, businessDisplayName]
  );


  const [rows, setRows] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [refetchTrigger, setRefetchTrigger] = React.useState(0);
  
  // Pagination & Server-side filtering
  const [currentPage, setCurrentPage] = React.useState<number>(0);
  const [pageSize, setPageSize] = React.useState<number>(20);
  const [totalElements, setTotalElements] = React.useState<number>(0);
  const [totalPages, setTotalPages] = React.useState<number>(0);
  
  const [payConfirmOpen, setPayConfirmOpen] = React.useState(false);
  const [toPayId, setToPayId] = React.useState<string | number | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [toDeleteId, setToDeleteId] = React.useState<string | number | null>(
    null,
  );

  React.useEffect(() => {
    const fetchUnits = async () => {
      console.log("fetchUnits called with venueId:", businessId);
      setLoadingUnits(true);
      try {
        if (!businessId) {
          console.error("No venue ID available, businessId:", businessId);
          setUnits([]);
          return;
        }

        const response = await getAllBranches(businessId);

        let unitsData = [];
        if (response && response.units) {
          unitsData = response.units;
        } else if (response && response.data) {
          unitsData = response.data;
        } else if (Array.isArray(response)) {
          unitsData = response;
        }

        console.log("Processed units data:", unitsData);
        const normalizedUnits = Array.isArray(unitsData) ? unitsData : [];
        setUnits(normalizedUnits);

        // Default to "all" when no selection exists
        setSelectValue((prev) => {
          const hasSelection = Array.isArray(prev)
            ? prev.length > 0
            : Boolean(prev);
          if (hasSelection) return prev;
          return "all";
        });
      } catch (error) {
        console.error("Error fetching units:", error);
        setUnits([]);
      } finally {
        setLoadingUnits(false);
      }
    };

    if (businessId) {
      fetchUnits();
    } else {
      console.log("No venueId available, skipping branch fetch");
    }
  }, [businessId]);

  React.useEffect(() => {
    setLoadingAccounts(true);
    const fetchAccounts = async () => {
      try {
        const accountsData = await listAccounts();
        const expenseAccounts = (accountsData || []).filter((a: any) => {
          const code = String(a?.code || "").toUpperCase();
          return code === "EXPENSE";
        });
        setAccounts(expenseAccounts);
      } catch (error) {
        console.error("Error fetching accounts:", error);
        setAccounts([]);
      } finally {
        setLoadingAccounts(false);
      }
    };
    fetchAccounts();
  }, []);

  React.useEffect(() => {
    let mounted = true;
    const loadProducts = async () => {
      setLoadingProducts(true);
      try {
        const res = await fetchProducts();
        const data = (res as any)?.data || res;
        const list = Array.isArray(data) ? data : data?.products || [];
        if (mounted) setProducts(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error("Error fetching products:", error);
        if (mounted) setProducts([]);
      } finally {
        if (mounted) setLoadingProducts(false);
      }
    };
    loadProducts();
    return () => {
      mounted = false;
    };
  }, []);

  React.useEffect(() => {
    const fetchExpenses = async () => {
      if (!businessId) {
        setRows([]);
        return;
      }

      setLoading(true);

      try {
        // Build query parameters for backend
        const normalizedRange = normalizeDateRange(dateRange as any);
        const params: any = {
          businessId,
          from: normalizedRange.start,
          to: normalizedRange.end,
          page: String(currentPage),
          size: String(pageSize),
        };

        // Respect "all": only send unitId when a specific unit is chosen
        const resolvedUnitId = (() => {
          if (selectValue && String(selectValue) !== "all") return selectValue;
          return null;
        })();

        if (resolvedUnitId) {
          params.unitId = String(resolvedUnitId);
        }

        console.log("Expenses API request params:", params);

        const res = await listExpenses(params);
        console.log("Expenses API response:", res);
        
        // Extract response data (backend should return optimized structure)
        const apiResponse = (res as any)?.data || res;
        const data = apiResponse?.expenses || apiResponse?.content || (Array.isArray(res) ? res : []);
        const totalElementsCount = apiResponse?.totalElements || data.length;
        const totalPagesCount = apiResponse?.totalPages || 1;

        // Store pagination info
        setTotalElements(totalElementsCount);
        setTotalPages(totalPagesCount);

        const unitNameById = new Map<string, string>(
          (units || []).map((u) => [
            String(u.id || u._id),
            u.name || u.title || String(u.id || u._id),
          ])
        );

        // SIMPLIFIED DATA MAPPING
        const mappedRows = data.map((e: any) => {
          const unitIdFromMeta =
            e?.metadata?.unitId ||
            e?.metadata?.unit_id ||
            e?.unitId ||
            e?.unit_id ||
            e?.unit?.id;

          const branchLabel =
            (unitIdFromMeta && unitNameById.get(String(unitIdFromMeta))) ||
            "N/A";

          return {
            id: e.id,
            description: e?.description || "Expense",
            amount: e.amount,
            title: e.title,
            notes: e.notes,
            createdAt: e.createdAt || e.updatedAt || new Date().toISOString(),
            status: e.status || "DRAFT",
            branch: branchLabel,
            unitId: unitIdFromMeta ? String(unitIdFromMeta) : "all",
            accountId: e?.accountId ?? e?.account?.id ?? "",
            metadata: e?.metadata || {},
          };
        });

        setRows(mappedRows);
      } catch (err) {
        console.error("Failed to fetch expenses:", err);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchExpenses();
  }, [currentPage, pageSize, selectValue, businessId, dateRange, refetchTrigger]);

  const filteredRows = React.useMemo(() => {
    const q = String(search || "").toLowerCase().trim();
    if (!q) return rows;

    return rows.filter((r: any) => {
      const haystack = [
        r.title,
        r.description,
        r.amount,
        r.createdAt,
        r.branch,
        r.note,
        r.createdBy,
      ]
        .filter(Boolean)
        .map((v: any) => String(v).toLowerCase())
        .join(" ");

      return haystack.includes(q);
    });
  }, [rows, search]);

  const openEditForId = (id: string | number) => {
    const found = rows.find((r) => String(r.id) === String(id));
    if (found) {
      setEditingRow(found);
      setExpenseDialogTab(found?.metadata?.type === "PURCHASE" ? 0 : 0);
      setEditOpen(true);
    }
  };

  const wiredActions = Actions({
     handleOpenEditModal: { handler: openEditForId },
    onApprove: {
      handler: async (id) => {
        try {
          await approveExpense(Number(id));
          toast.success("Expense approved successfully");
          setRefetchTrigger((prev) => prev + 1);
        } catch (error) {
          console.error(error);
          showErrorToast(getErrorMessage(error, "Failed to approve expense"));
        }
      },
      visible: (row: any) => row.status === "RECORDED",
    },
    onPay: {
      handler: async (id: number | string) => {
        setToPayId(id);
        setPayConfirmOpen(true);
      },
      visible: (row: any) => row.status === "APPROVED",
    },
    onDelete: {
      handler: (id: number | string) => {
        setToDeleteId(id);
        setDeleteConfirmOpen(true);
      },
    },
  });

  const exportCsv = React.useCallback(() => {
    if (!rows || rows.length === 0) return;
    const keys = [
      "title",
      "description",
      "amount",
      "branch",
      "status",
      "notes",
      "createdAt",
    ];
    const header = keys.join(",");
    const lines = rows.map((r: any) => {
      const row: Record<string, any> = {
        title: r.title ?? "-",
        description: r.description ?? "-",
        amount: r.amount ?? "-",
        branch: r.branch ?? "-",
        status: r.status ?? "-",
        notes: r.notes ?? "-",
        createdAt: r.createdAt ?? "-",
      };
      return keys
        .map((k) => `"${String(row[k] ?? "-").replace(/"/g, '""')}"`)
        .join(",");
    });
    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `export-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [rows]);
  const getUnitId = (u: any) => u.id ?? u.unit_id; // normalize
  return (
    <Box sx={{ width: "100%", maxWidth: "90vw", overflowX: "hidden" }}>
      <Box sx={{ mt: { xs: 2, sm: 3 } }}>
        <PageHeader
          title="Expenses"
          subtitle="Track what the venue spends."
        />
        <SearchHeaderContainer
          search={search}
          setSearch={setSearch}
          dateRangeConfig={{
            value: dateRange ?? undefined,
            onChange: (v: any) => setDateRange(v),
            fullWidth: false,
          }}
          selectConfig={{
            options: [
              { label: "All", value: "all" },
              ...units.map((unit) => ({
                label: unit.name || "Unknown Branch",
                value: getUnitId(unit),
              })),
            ],
            value: selectValue,
            onChange: (v: any) => {
              setSelectValue(v);
            },
            placeholder: "Select branch",
            label: "Select Branch",
            size: "medium",
            searchable: false,
            clearable: true,
            multiSelect: false,
            sx: {
              backgroundColor: COLORS.WhiteBackground,
            },
          }}
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.MainColor,
              startIcon: <AddIcon />,
              onClick: () => {
                setEditingRow(null);
                setExpenseDialogTab(0);
                setEditOpen(true);
              },
              textColor: COLORS.WhiteText,
              minHeight: 36,
              maxHeight: 36,
              sx: {
                width: { xs: "96%", sm: 36 },
                minWidth: { sm: 36 },
                marginRight: { xs: 1.5, sm: 0 },
              },
            },
            {
              label: "Export CSV",
              onClick: () => Promise.resolve(exportCsv()),
              startIcon: <FileDownloadIcon />,
              variant: "contained",
              bgColor: COLORS.MainColor,
              textColor: "#fff",
              showExportProgress: true,
              sx: {
                width: { xs: "96%", sm: "auto" },
                marginRight: { xs: 1.5, sm: 0 },
              },
            },
          ]}
        />
        <CustomTable<any>
          filters={{ page: currentPage, size: pageSize }}
          clientSidePagination={false}
          loading={loading}
          actions={wiredActions}
          fields={[
            { key: "title", header: "Title" },
            { key: "description", header: "Description" },
            { key: "amount", header: "Amount" },
            { key: "branch", header: "Branch" },
            {
              key: "status",
              header: "Status",
              render: (r: any) => {
                const status = String(r?.status || "").toUpperCase();
                const isPaid = status === "PAID";
                return (
                  <Box
                    sx={{
                      px: 1.2,
                      py: 0.4,
                      borderRadius: 10,
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#fff",
                      backgroundColor: isPaid ? "#16a34a" : "#9ca3af",
                      display: "inline-block",
                      minWidth: 70,
                      textAlign: "center",
                    }}
                  >
                    {status || "-"}
                  </Box>
                );
              },
            },
            { key: "notes", header: "Notes" },
            { key: "createdAt", header: "Date" },

            // { key: "createdBy", header: "Created By" },
          ]}
          fetchFunc={async (params?: any) => {
            // Update state when table requests a new page
            if (params?.page !== undefined && params.page !== currentPage) {
              setCurrentPage(params.page);
            }
            if (params?.size !== undefined && params.size !== pageSize) {
              setPageSize(params.size);
              setCurrentPage(0); // Reset to first page when changing page size
            }
            
            // Return current data
            return {
              content: filteredRows,
              number: currentPage,
              size: pageSize,
              totalElements: totalElements,
              totalPages: totalPages,
            };
          }}
        />
        <Box
          sx={{
            backgroundColor: COLORS.WhiteBackground,
            borderRadius: "0 0 10px 10px",
            padding: "16px",
            marginTop: "-10px",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 2,
          }}
        >
          <CustomLabel
            text="Total Amount:"
            sx={{ color: COLORS.typographyColor }}
          />
          <CustomLabel
            text={new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(
              rows
                .filter((r: any) => {
                  // Filter by search
                  const normalizedQuery = String(search || "")
                    .toLowerCase()
                    .trim();

                  if (normalizedQuery) {
                    const haystack = [
                      r.title,
                      r.description,
                      r.amount,
                      r.createdAt,
                      r.branch,
                      r.note,
                      r.createdBy,
                    ]
                      .filter(Boolean)
                      .map((v: any) => String(v).toLowerCase())
                      .join(" ");

                    if (!haystack.includes(normalizedQuery)) return false;
                  }

                  // Filter by date range
                  if (dateRange) {
                    const normalizedRange = normalizeDateRange(dateRange as any);
                    const rowDate = new Date(r.createdAt);
                    let startDate: Date | null = null;
                    let endDate: Date | null = null;

                    if (normalizedRange.start)
                      startDate = new Date(normalizedRange.start);
                    if (normalizedRange.end) endDate = new Date(normalizedRange.end);

                    if (startDate && rowDate < startDate) return false;
                    if (endDate && rowDate > endDate) return false;
                  }

                  return true;
                })
                .reduce((sum: number, r: any) => {
                  const amount = Number(r.amount) || 0;
                  return sum + amount;
                }, 0),
            )}
            fontWeight="bold"
            sx={{ color: COLORS.MainColor }}
          />
        </Box>
        <GenericFormDialog
          open={editOpen}
          onClose={() => {
            setEditOpen(false);
            setEditingRow(null);
          }}
          title={
            editingRow
              ? editingRow?.metadata?.type === "PURCHASE"
                ? "Edit Purchase"
                : "Edit Expense"
              : "Add Expense"
          }
          submitLabel={editingRow ? "Update" : "Create"}
          fields={editingRow && editingRow?.metadata?.type !== "PURCHASE" ? formFields : undefined}
          tabs={
            editingRow
              ? editingRow?.metadata?.type === "PURCHASE"
                ? [{ label: "Purchases", fields: purchaseFields }]
                : undefined
              : [
                  { label: "Add", fields: formFields },
                  { label: "Purchases", fields: purchaseFields },
                ]
          }
          initialTab={expenseDialogTab}
          onTabChange={setExpenseDialogTab}
          initialValues={expenseInitialValues}
          disableSubmitUntilValid
          onSubmit={async (values) => {
            try {
              const selectedScope = String(values.unitId || "all");
              const baseMeta =
                selectedScope === "all"
                  ? { businessId }
                  : { businessId, unitId: selectedScope };

              const isPurchase =
                (editingRow && editingRow?.metadata?.type === "PURCHASE") ||
                (!editingRow && expenseDialogTab === 1);
              const lines: InvoiceItem[] = Array.isArray(values.lines)
                ? values.lines
                : [];
              const purchaseItems: ExpenseItemInput[] = lines.map((line) => ({
                description:
                  line.serviceLabel || String(line.service || "Item"),
                amount: Number(line.totalPrice) || 0,
              }));

              const standardItems: ExpenseItemInput[] = Array.isArray(values.items)
                ? values.items.map((i: any) => ({
                    description: i.description || "",
                    amount: Number(i.amount),
                  }))
                : [];

              const clampPercent = (value: number) =>
                Math.min(100, Math.max(0, value));

              const purchaseSubtotal = lines.reduce(
                (sum, line) => sum + (Number(line.totalPrice) || 0),
                0
              );
              const purchaseDiscountPercent = clampPercent(
                Number(values.discount) || 0
              );
              const purchaseVatPercent = clampPercent(
                Number(values.vat) || 0
              );
              const purchaseDiscountAmount =
                (purchaseSubtotal * purchaseDiscountPercent) / 100;
              const purchaseAmountAfterDiscount =
                purchaseSubtotal - purchaseDiscountAmount;
              const purchaseVatAmount =
                (purchaseAmountAfterDiscount * purchaseVatPercent) / 100;
              const purchaseNetAmount =
                Math.round(
                  (purchaseAmountAfterDiscount + purchaseVatAmount) * 100
                ) / 100;

              const payload: CreateExpensePayload = isPurchase
                ? {
                    amount: purchaseNetAmount,
                    currency: "USD",
                    title: "Purchases",
                    notes: values.purchaseNotes || "",
                    accountId: Number(values.accountId),
                    description:
                      values.description ||
                      (values.supplierName
                        ? `Purchases from ${values.supplierName}`
                        : "Purchases"),
                    items: purchaseItems,
                    metadata: {
                      type: "PURCHASE",
                      supplierName: values.supplierName || null,
                      supplierPhone: values.supplierPhone || null,
                      supplierOrgUserId: values.supplierOrgUserId || null,
                      invoiceNumber: values.invoiceNumber || null,
                      invoice_nb: values.invoiceNumber || values.invoice_nb || null,
                      lines,
                      discountPercent: purchaseDiscountPercent,
                      vatPercent: purchaseVatPercent,
                      ...baseMeta,
                    },
                  }
                : {
                    amount: Number(values.amount),
                    currency: "USD",
                    title: values.title,
                    notes: values.notes || "",
                    accountId: Number(values.accountId),
                    description: values.description || "",
                    items: standardItems,
                    metadata: baseMeta,
                  };

              if (editingRow?.id) {
                await updateExpense(Number(editingRow.id), payload);
                toast.success("Expense updated successfully!");
                setRefetchTrigger((prev) => prev + 1);
                setEditOpen(false);
                setEditingRow(null);
                return;
              }

              const autoPay = Boolean(values.autoPay);
              const created = (await createExpense({
                ...payload,
                autoPay,
              })) as any;

              // Try to auto-approve the newly created expense
              const createdId =
                (created && created.id) ||
                created?.expense?.id ||
                created?.data?.id;

              if (autoPay) {
                // Already PAID; billing rejects approving a paid expense
                toast.success("Expense created and paid successfully!");
              } else if (createdId !== undefined && createdId !== null) {
                try {
                  await approveExpense(Number(createdId));
                  toast.success("Expense created and approved successfully!");
                } catch (err) {
                  console.error("Auto-approve failed:", err);
                  toast.warn(
                    "Expense created, but auto-approve failed. Please approve manually.",
                  );
                }
              } else {
                toast.warn(
                  "Expense created, but could not determine ID to auto-approve.",
                );
              }

              setRefetchTrigger((prev) => prev + 1);
              setEditOpen(false);
              setEditingRow(null);
            } catch (error) {
              console.error(error);
              showErrorToast(
                getErrorMessage(
                  error,
                  editingRow ? "Failed to update expense." : "Failed to create expense."
                )
              );
            }
          }}
        />

        <GenericFormDialog
          open={productDialogOpen}
          onClose={() => setProductDialogOpen(false)}
          title="Add Product"
          fields={productFormFields}
          initialValues={{ packageType: "UNIT" }}
          disableSubmitUntilValid
          onSubmit={async (values) => {
            try {
              const payload = {
                name: values.name,
                packageType: values.packageType || "UNIT",
                costPrice:
                  values.cost_price !== undefined && values.cost_price !== ""
                    ? Number(values.cost_price)
                    : null,
                salePrice:
                  values.sale_price !== undefined && values.sale_price !== ""
                    ? Number(values.sale_price)
                    : null,
              };
              const created = await createProduct(payload);
              const product = (created as any)?.data ?? created;
              setProducts((prev) => [product, ...prev]);
              toast.success("Product added successfully!");
              setProductDialogOpen(false);
            } catch (error) {
              console.error(error);
              showErrorToast(getErrorMessage(error, "Failed to add product."));
            }
          }}
        />

        <CustomDialog
          open={payConfirmOpen}
          onClose={() => {
            setPayConfirmOpen(false);
            setToPayId(null);
          }}
          title="Confirm Payment"
        >
          <Box sx={{ p: 2 }}>
            <CustomLabel
              text="Are you sure you want to mark this expense as paid?"
              sx={{ mb: 2 }}
            />
            {toPayId &&
              (() => {
                const expense = rows.find((exp) => exp.id === toPayId);
                if (expense) {
                  return (
                    <Box
                      sx={{
                        mb: 2,
                        p: 2,
                        backgroundColor: "#f5f5f5",
                        borderRadius: 1,
                      }}
                    >
                      <Box sx={{ mb: 1 }}>
                        <CustomLabel
                          text="Description: "
                          fontWeight="bold"
                          sx={{ display: "inline" }}
                        />
                        <CustomLabel
                          text={expense.description || "N/A"}
                          sx={{ display: "inline" }}
                        />
                      </Box>
                      <Box sx={{ mb: 1 }}>
                        <CustomLabel
                          text="Amount: "
                          fontWeight="bold"
                          sx={{ display: "inline" }}
                        />
                        <CustomLabel
                          text={`$${expense.amount || 0}`}
                          sx={{ display: "inline" }}
                        />
                      </Box>
                      <Box>
                        <CustomLabel
                          text="Date: "
                          fontWeight="bold"
                          sx={{ display: "inline" }}
                        />
                        <CustomLabel
                          text={formatTableDate(expense.createdAt)}
                          sx={{ display: "inline" }}
                        />
                      </Box>
                    </Box>
                  );
                }
                return null;
              })()}
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <CustomButton
                label="Cancel"
                onClick={() => {
                  setPayConfirmOpen(false);
                  setToPayId(null);
                }}
                bgColor="#9e9e9e"
                textColor="#fff"
              />
              <CustomButton
                label="Confirm Payment"
                onClick={async () => {
                  if (toPayId === null) return;
                  try {
                    await payExpense(Number(toPayId));
                    toast.success("Expense marked as paid successfully!");
                    setRefetchTrigger((prev) => prev + 1);
                    setPayConfirmOpen(false);
                    setToPayId(null);
                  } catch (error) {
                    console.error("Error paying expense:", error);
                    showErrorToast(
                      getErrorMessage(error, "Failed to mark expense as paid.")
                    );
                    setPayConfirmOpen(false);
                    setToPayId(null);
                  }
                }}
                bgColor="#2e7d32"
                textColor="#fff"
              />
            </Box>
          </Box>
        </CustomDialog>

        <CustomDialog
          open={deleteConfirmOpen}
          onClose={() => {
            setDeleteConfirmOpen(false);
            setToDeleteId(null);
          }}
          title="Delete Expense"
        >
          <Box sx={{ p: 2 }}>
            <CustomLabel
              text="Are you sure you want to delete this expense? This action cannot be undone."
              sx={{ mb: 2 }}
            />
            {toDeleteId &&
              (() => {
                const expense = rows.find(
                  (exp) => String(exp.id) === String(toDeleteId),
                );
                if (expense) {
                  return (
                    <Box
                      sx={{
                        mb: 2,
                        p: 2,
                        backgroundColor: "#f5f5f5",
                        borderRadius: 1,
                      }}
                    >
                      <Box sx={{ mb: 1 }}>
                        <CustomLabel
                          text="Title: "
                          fontWeight="bold"
                          sx={{ display: "inline" }}
                        />
                        <CustomLabel
                          text={expense.title || "N/A"}
                          sx={{ display: "inline" }}
                        />
                      </Box>
                      <Box sx={{ mb: 1 }}>
                        <CustomLabel
                          text="Description: "
                          fontWeight="bold"
                          sx={{ display: "inline" }}
                        />
                        <CustomLabel
                          text={expense.description || "N/A"}
                          sx={{ display: "inline" }}
                        />
                      </Box>
                      <Box>
                        <CustomLabel
                          text="Amount: "
                          fontWeight="bold"
                          sx={{ display: "inline" }}
                        />
                        <CustomLabel
                          text={`$${expense.amount || 0}`}
                          sx={{ display: "inline" }}
                        />
                      </Box>
                    </Box>
                  );
                }
                return null;
              })()}
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <CustomButton
                label="Cancel"
                onClick={() => {
                  setDeleteConfirmOpen(false);
                  setToDeleteId(null);
                }}
                bgColor="#9e9e9e"
                textColor="#fff"
              />
              <CustomButton
                label="Delete"
                onClick={async () => {
                  if (!toDeleteId) return;
                  try {
                    await deleteExpense(Number(toDeleteId));
                    toast.success("Expense deleted successfully");
                    setRefetchTrigger((prev) => prev + 1);
                  } catch (error) {
                    console.error("Failed to delete expense", error);
                    toast.error("Failed to delete expense");
                  } finally {
                    setDeleteConfirmOpen(false);
                    setToDeleteId(null);
                  }
                }}
                bgColor="#d32f2f"
                textColor="#fff"
              />
            </Box>
          </Box>
        </CustomDialog>
      </Box>
    </Box>
  );
};

export default Expenses;
